"use client";

import { useState } from "react";
import { siteConfig } from "@/config/site";
import { useTranslation } from "@/i18n";
import {
    SectionWrapper,
    SectionEyebrow,
    SectionHeadline,
} from "@/components/ui/SectionWrapper";
import { FadeIn } from "@/components/ui/AnimatedSection";
import Image from "next/image";

function getYouTubeIds(url: string): { embedUrl: string; videoId: string } | null {
    const match = url.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/
    );
    if (!match) return null;
    const videoId = match[1];
    return {
        embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`,
        videoId,
    };
}

export function Trailer() {
    const [isPlaying, setIsPlaying] = useState(false);
    const { t } = useTranslation();

    const ytData = getYouTubeIds(siteConfig.trailerUrl);
    if (!ytData) return null;

    return (
        <SectionWrapper id="trailer" className="py-(--section-y-lg)">
            <div className="text-center max-w-3xl mx-auto mb-12">
                <FadeIn>
                    <SectionEyebrow className="text-center">
                        {t.trailer.eyebrow}
                    </SectionEyebrow>
                    <SectionHeadline className="text-center">
                        {t.trailer.headline}
                    </SectionHeadline>
                </FadeIn>
            </div>

            <FadeIn delay={0.2}>
                <div className="relative w-full max-w-4xl mx-auto rounded-xl overflow-hidden border border-border bg-surface shadow-2xl shadow-void/50 group cursor-pointer" onClick={() => setIsPlaying(true)}>
                    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                        {!isPlaying ? (
                            <>
                                <Image
                                    src={`https://img.youtube.com/vi/${ytData.videoId}/maxresdefault.jpg`}
                                    alt="Expedition 744 - Official Trailer thumbnail"
                                    fill
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 896px"
                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-void/30 group-hover:bg-void/10 transition-colors duration-300" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-16 h-16 md:w-20 md:h-20 bg-mars text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(212,96,58,0.5)] group-hover:scale-110 transition-transform duration-300">
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 ml-1">
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <iframe
                                src={ytData.embedUrl}
                                title="Expedition 744 - Official Trailer"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                            />
                        )}
                    </div>
                </div>
            </FadeIn>
        </SectionWrapper>
    );
}
